import * as React from 'react'
import moment from 'moment'

import {
  UserListItem,
  UserRoleAssignment,
} from '@/lib/api/company/configuration/adminPanel/users'
import { useTable } from '@/modules/drivers/context/TableContext'
import Checkbox from '@/modules/drivers/shared/Checkbox'
import {
  RoleManagementPopover,
  companyRolesOf,
} from '@/modules/users/pages/components/RoleManagementPopover'
import { AdminIcon } from '@/modules/shared/components'
import { ServiceAccountRoleCell } from '@/modules/shared/components/serviceAccount/ServiceAccountUserRoles'
import { ServiceAccountUser } from '@/modules/shared/query-client/useServiceAccountAdmin'
import { TIME_FORMAT } from 'app/constants/date'

type Props = {
  user: UserListItem
  companyId: string
  onRolesUpdate: (
    user: UserListItem,
    roles: UserRoleAssignment[],
  ) => Promise<void>
  onEdit: (user: UserListItem) => void
  onSendInvitation: (user: UserListItem) => void
  // Set when the company is a service account: access comes from its roster, roles are read-only.
  serviceAccount?: { membership?: ServiceAccountUser }
}

/**
 * Wiersz listy użytkowników w panelu admina.
 *
 * Odpowiednik wiersza z `/et` (`modules/users/pages/components/UserRow`), ale z kolumnami,
 * które ma panel: nazwa użytkownika, firma, kto dodał i akcje po prawej. Zaznaczenie idzie
 * przez ten sam `TableContext`, więc pasek akcji nad tabelą działa tak samo.
 */
export const AdminUserRow: React.FC<Props> = ({
  user,
  companyId,
  onRolesUpdate,
  onEdit,
  onSendInvitation,
  serviceAccount,
}) => {
  const { toggleId, selectedGroup } = useTable()
  const isChecked = selectedGroup?.ids.includes(user.id) ?? false

  // `user.roles` ma wpis na każdą firmę użytkownika. W firmie liczą się tylko jej role;
  // w widoku globalnym każda rola raz, z firmami w podpowiedzi.
  const roles = companyId
    ? companyRolesOf(user, companyId).map((role) => ({
        ...role,
        title: undefined,
      }))
    : Array.from(
        (user.roles ?? [])
          .flatMap((assignment) =>
            assignment.roles.map((role) => ({
              role,
              company: assignment.companyName,
            })),
          )
          .reduce((byId, { role, company }) => {
            const entry = byId.get(role.id) ?? {
              ...role,
              companies: [] as string[],
            }
            if (company) entry.companies.push(company)
            return byId.set(role.id, entry)
          }, new Map<number, { id: number; name: string; companies: string[] }>())
          .values(),
      ).map((role) => ({ ...role, title: role.companies.join(', ') }))
  const fullName = [user.firstName, user.surname].filter(Boolean).join(' ')

  const formatDate = (date: string | null) =>
    date ? moment(date).format(TIME_FORMAT) : '-'

  return (
    <tr
      className={`tw-cursor-pointer tw-border-b tw-border-base-300 hover:tw-bg-primary/10 ${
        isChecked ? 'tw-bg-primary/10 tw-font-medium' : ''
      }`}
      onClick={() => toggleId(user.id, true)}
      onDoubleClick={() => onEdit(user)}
    >
      <td className="tw-w-12 tw-px-4 tw-py-3">
        <label className="tw-inline-flex tw-items-center">
          <Checkbox
            checked={isChecked}
            onChange={() => toggleId(user.id, true)}
          />
        </label>
      </td>
      <td className="tw-px-2 tw-py-3 tw-text-center">
        <AdminIcon
          name={user.locked ? 'not-connected' : 'connected'}
          title={user.locked ? 'Zablokowany' : 'Aktywny'}
        />
      </td>
      <td className="tw-px-4 tw-py-3 tw-text-sm">{fullName || '-'}</td>
      <td className="tw-px-4 tw-py-3 tw-text-sm">{user.companyName || '-'}</td>
      <td className="tw-px-4 tw-py-3 tw-text-sm">
        {serviceAccount ? (
          <ServiceAccountRoleCell
            roles={companyRolesOf(user, companyId)}
            membership={serviceAccount.membership}
          />
        ) : (
          <div className="tw-inline-flex tw-items-center tw-gap-1">
            <div className="tw-inline-flex tw-flex-wrap tw-gap-1">
              {roles.map((role) => (
                <span
                  key={role.id}
                  title={role.title}
                  className="badge badge-sm badge-accent"
                >
                  {role.name}
                </span>
              ))}
              {roles.length === 0 && (
                <span className="tw-text-content-muted">-</span>
              )}
            </div>
            {companyId && (
              <RoleManagementPopover
                user={user}
                companyId={companyId}
                onRolesUpdate={onRolesUpdate}
              />
            )}
          </div>
        )}
      </td>
      <td className="tw-px-4 tw-py-3 tw-text-sm">{user.login}</td>
      <td className="tw-px-4 tw-py-3 tw-text-sm">{user.createdBy || '-'}</td>
      <td className="tw-whitespace-nowrap tw-px-4 tw-py-3 tw-text-sm">
        {formatDate(user.createdDate)}
      </td>
      <td className="tw-w-12 tw-px-2 tw-py-3">
        <AdminIcon
          name={user.isActivated ? 'send' : 'send-invalid'}
          title="Wyślij zaproszenie"
          onClick={(event) => {
            event.stopPropagation()
            onSendInvitation(user)
          }}
        />
      </td>
      <td className="tw-w-12 tw-px-2 tw-py-3">
        <AdminIcon
          name="edit"
          title="Edytuj"
          onClick={(event) => {
            event.stopPropagation()
            onEdit(user)
          }}
        />
      </td>
    </tr>
  )
}
